import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { WhatsAppStatus } from '../types';
import { whatsappAccountService } from '../services/whatsappAccountService';
import toast from 'react-hot-toast';

interface SocketContextType {
  socket: Socket | null;
  connected: boolean;
  whatsappStatus: WhatsAppStatus | null;
  newRideCount: number;
  clearNewRideCount: () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatus | null>(null);
  const [newRideCount, setNewRideCount] = useState(0);

  const refreshWhatsAppStatus = async () => {
    try {
      const accounts = await whatsappAccountService.getAccounts();
      const anyReady = accounts.some((account) => Boolean(account.isReady));
      const anyConnected = accounts.some((account) => Boolean(account.isConnected || account.isReady));
      const latestActivity = accounts
        .map((account) => account.lastActivity)
        .filter(Boolean)
        .map((value) => new Date(value as Date | string))
        .sort((left, right) => right.getTime() - left.getTime())[0];

      setWhatsappStatus({
        isConnected: anyConnected,
        isReady: anyReady,
        lastActivity: latestActivity ? latestActivity.toISOString() : undefined,
      });
    } catch (error) {
      console.error('Failed to refresh WhatsApp status:', error);
    }
  };

  const resolveSocketUrl = () => {
    const explicit = process.env.REACT_APP_SOCKET_URL;
    // Only use the explicit value when it makes sense for the current browser host.
    // A localhost value baked into a production build would route connections to the
    // user's own machine instead of the server, so we skip it in that case.
    const browserIsLocal =
      typeof window !== 'undefined' && window.location.hostname === 'localhost';
    if (explicit && (browserIsLocal || !explicit.includes('localhost'))) {
      return explicit;
    }

    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl && !apiUrl.includes('localhost')) {
      return apiUrl.replace(/\/api\/?$/, '');
    }

    // Same-server deployment: backend serves the React build, so the socket lives
    // at the same origin the browser loaded the page from.
    return typeof window !== 'undefined' ? window.location.origin : 'http://localhost:4000';
  };

  useEffect(() => {
    if (user) {
      const socketUrl = resolveSocketUrl();
      const newSocket = io(socketUrl, {
        path: '/socket.io',
        auth: {
          userId: user.id,
          role: user.role,
        },
        transports: ['websocket', 'polling'],
        withCredentials: true,
        timeout: 10000,
      });

      console.log('🔌 Connecting socket to', socketUrl);

      newSocket.on('connect', () => {
        console.log('🔌 Connected to server');
        setConnected(true);
        refreshWhatsAppStatus();
        
        // Join user-specific rooms
        newSocket.emit('join', {
          userId: user.id,
          role: user.role,
        });
      });

      newSocket.on('disconnect', () => {
        console.log('🔌 Disconnected from server');
        setConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        console.error('Socket connect_error:', error.message, error);
        setConnected(false);
      });

      newSocket.on('connected', (data) => {
        console.log('✅ Socket connection confirmed:', data);
      });

      // WhatsApp events
      newSocket.on('whatsapp:statusUpdate', (status: WhatsAppStatus) => {
        setWhatsappStatus(status);
      });

      newSocket.on('whatsapp:statusChanged', (status: WhatsAppStatus) => {
        setWhatsappStatus(status);
        if (status.isConnected) {
          toast.success('WhatsApp connected!');
        } else {
          toast.error('WhatsApp disconnected');
        }
      });

      const handleAccountStatusChange = () => {
        refreshWhatsAppStatus();
      };

      newSocket.on('whatsapp:accountStatusUpdate', handleAccountStatusChange);
      newSocket.on('whatsapp:accountReady', handleAccountStatusChange);

      // Ride events
      newSocket.on('newRide', (ride: any) => {
        setNewRideCount(prev => prev + 1);
        toast.success(`New ride request from ${ride.contactInfo?.name || 'Unknown'}!`);
      });

      newSocket.on('rideUpdate', (data: any) => {
        toast(`Ride ${data.rideId} updated`);
      });

      newSocket.on('ride:statusChanged', (data: any) => {
        toast(`Ride status changed to ${data.status}`);
      });

      newSocket.on('ride:assigned', (data: any) => {
        toast.success('New ride assigned to you!');
      });

      // Error handling
      newSocket.on('error', (error: any) => {
        console.error('Socket error:', error);
        toast.error('Connection error occurred');
      });

      setSocket(newSocket);
      refreshWhatsAppStatus();

      return () => {
        newSocket.off('whatsapp:accountStatusUpdate', handleAccountStatusChange);
        newSocket.off('whatsapp:accountReady', handleAccountStatusChange);
        newSocket.close();
        setSocket(null);
        setConnected(false);
      };
    }
  }, [user]);

  const clearNewRideCount = () => {
    setNewRideCount(0);
  };

  const value: SocketContextType = {
    socket,
    connected,
    whatsappStatus,
    newRideCount,
    clearNewRideCount,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
